// External component imports
import { Accordion, AccordionDetails, AccordionGroup, AccordionSummary } from '@interstate/components/AccordionGroup';
import {
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon
} from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Typography } from '@interstate/components/Typography';
import { Link } from '@interstate/components/Link';

// Local style imports
import { ComplianceLinkContainer, ContainerBox, IconTextContainer } from './ComplianceAccordian.style';

// Constants and types
import { COMPLIANCE, DOCUMENT_VERIFICATION_TRUST_KEY, FINAL_ASSESSMENT_KEY } from '../shared/constants';
import type { ComplianceSections, BinaryValues } from '../shared/types';
import { ComplianceStatus, ComplianceType } from '../shared/types';

// Local component imports
import { OFACDetails } from './OFAC/OFACDetails';
import { RedFlagsDetails } from './RedFlag/RedFlagsDetails';
import { ConsistencyCheckDetails } from './ConsistencyCheck/ConsistencyCheck';
import { IdVerificationDetails } from './IdVerification/IdVerification';
import { SyntheticIdVerificationDetails } from './SyntheticIdVerification/SyntheticIdVerification';

// Local utility imports
import { createStatusConfig, getStatus, isValidSection } from '../shared/utils';
import { useSelector } from 'react-redux';
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { dealLockHooks } from '../../hooks';

export const ComplianceAccordion = () => {
    const theme = useInterstateTheme();

    // Get locked state to disable Dealer Track link when deal is finalized
    const isLocked = dealLockHooks.useIsDealLocked();
    // Disable Dealer Track link for quotes/deals that haven't been saved yet
    const isDealXgRecordMissed = useSelector(offerReduxSelectors.getIsDealXgRecordMissed);
    const hasCustomer = Boolean(useSelector(offerSelectors.getOfferCommonConsumerId));
    const isDealerTrackLinkDisabled = isLocked || isDealXgRecordMissed || !hasCustomer;

    // Get complianceData from the store
    const participantMatch = useSelector(complianceSelectors.getComplianceParticipantForShopper);

    const piiConsistencyStatus = useSelector(complianceSelectors.getPIIConsistencyStatus);
    const piiConsistencyError = useSelector(complianceSelectors.getPIIConsistencyErrorMessage);

    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);

    const compliance = participantMatch?.compliance ?? {};
    const data = compliance as Partial<ComplianceSections>;

    //for Id Verification status
    const documentFindings = Array.isArray(data.idVerification?.findings) ? data.idVerification.findings : [];
    const documentVerificationTrustValue =
        documentFindings.find((item: any) => item.name === DOCUMENT_VERIFICATION_TRUST_KEY)?.value || '';

    //For synthetic ID Verification status
    const syntheticFindings = Array.isArray(data.syntheticIDVerification?.findings) ? data.syntheticIDVerification.findings : [];
    const finalAssesmentValue = syntheticFindings.find((item: any) => item.name === FINAL_ASSESSMENT_KEY)?.value || '';

    // Check if other compliance components exist before showing Consistency Check
    const hasOtherComplianceComponents = !!(data.OFAC || data.redFlags || data.idVerification || data.syntheticIDVerification);

    const accordionSections = [
        data.OFAC && {
            type: ComplianceType.OFAC,
            component: <OFACDetails />,
            status: data.OFAC.status
        },
        data.redFlags && {
            type: ComplianceType.RED_FLAG,
            component: <RedFlagsDetails />,
            status: data.redFlags.status
        },
        ...(isEcommerceDeal && hasOtherComplianceComponents && !piiConsistencyError
            ? [
                  {
                      type: ComplianceType.CONSISTENCY_CHECK,
                      component: <ConsistencyCheckDetails />,
                      status: piiConsistencyStatus // Use actual PII consistency status from store
                  }
              ]
            : []),
        data.idVerification && {
            type: ComplianceType.ID_VERIFICATION,
            component: <IdVerificationDetails />,
            status: getStatus(data.idVerification.status, documentVerificationTrustValue as BinaryValues)
        },
        data.syntheticIDVerification && {
            type: ComplianceType.SYNTHETIC_ID_VERIFICATION,
            component: <SyntheticIdVerificationDetails />,
            status: getStatus(data.syntheticIDVerification.status, finalAssesmentValue as BinaryValues)
        }
    ].filter(isValidSection);

    const statusConfig = {
        [ComplianceStatus.PASS]: createStatusConfig(
            CheckCircleIcon,
            theme.tokens.BaseColorGreen500,
            COMPLIANCE.STATUS_LABELS.PASSED,
            'card-check-icon'
        ),
        [ComplianceStatus.FAIL]: createStatusConfig(
            XCircleIcon,
            theme.tokens.BaseColorRed500,
            COMPLIANCE.STATUS_LABELS.FAILED,
            'x-circle-icon'
        ),
        [ComplianceStatus.ERROR]: createStatusConfig(
            ExclamationCircleIcon,
            theme.tokens.BaseColorRed500,
            COMPLIANCE.STATUS_LABELS.ERROR,
            'error-icon'
        ),
        [ComplianceStatus.NOT_STARTED]: createStatusConfig(
            ExclamationTriangleIcon,
            theme.tokens.BaseColorGold500,
            COMPLIANCE.STATUS_LABELS.NOT_STARTED,
            'not-started-icon'
        ),
        [ComplianceStatus.NOT_APPLICABLE]: createStatusConfig(
            ExclamationTriangleIcon,
            theme.tokens.BaseColorGold500,
            COMPLIANCE.STATUS_LABELS.NOT_APPLICABLE,
            'not-applicable-icon'
        ),
        [ComplianceStatus.STARTED]: createStatusConfig(
            ClockIcon,
            theme.tokens.BaseColorBlue500,
            COMPLIANCE.STATUS_LABELS.STARTED,
            'started-icon'
        ),
        [ComplianceStatus.COMPLETED]: createStatusConfig(
            CheckCircleIcon,
            theme.tokens.BaseColorGreen500,
            COMPLIANCE.STATUS_LABELS.COMPLETED,
            'completed-icon'
        ),
        [ComplianceStatus.UNDETERMINED]: createStatusConfig(
            ExclamationCircleIcon,
            theme.tokens.BaseColorGold500,
            COMPLIANCE.STATUS_LABELS.UNDETERMINED,
            'undetermined-icon'
        )
    };

    const defaultOpenIndices = accordionSections.reduce<number[]>((acc, section, index) => {
        if (section.status === ComplianceStatus.FAIL) acc.push(index);
        return acc;
    }, []);

    if (!hasOtherComplianceComponents || accordionSections.length === 0) {
        return null;
    }

    return (
        <AccordionGroup accordionType="multi-expanded" defaultOpen={defaultOpenIndices} data-testid="compliance-accordion">
            {accordionSections.map(({ type, component, status }) => {
                const sectionConfig = statusConfig[status as ComplianceStatus] ?? statusConfig[ComplianceStatus.FAIL];
                const { icon: Icon, color, label, testId } = sectionConfig;

                return (
                    <Accordion key={type}>
                        <AccordionSummary
                            data-testid={`compliance-accordion-summary-${type}`}
                            chevronPosition="left"
                            badge={
                                <IconTextContainer>
                                    {Icon && <Icon color={color} data-testid={testId} />}
                                    <span>{label}</span>
                                </IconTextContainer>
                            }
                        >
                            {/* Dynamic accordion label based on PII consistency status */}
                            <span>
                                {type === ComplianceType.CONSISTENCY_CHECK
                                    ? piiConsistencyStatus === 'PASS'
                                        ? 'Consistency Check'
                                        : 'Consistency Issue Check'
                                    : COMPLIANCE.ACCORDION_LABELS[type]}
                            </span>
                        </AccordionSummary>
                        <AccordionDetails>
                            {component}
                            <ContainerBox>
                                <ComplianceLinkContainer>
                                    {data.dealertrackDetailsHref && (
                                        <Typography variant="body-sm">
                                            {COMPLIANCE.DEALER_TRACK.DESCRIPTION}{' '}
                                            <Link
                                                data-testid="dealertrack-link"
                                                href={data.dealertrackDetailsHref}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                size="sm"
                                                sx={{
                                                    ...(isDealerTrackLinkDisabled && {
                                                        pointerEvents: 'none',
                                                        opacity: 0.5,
                                                        cursor: 'not-allowed',
                                                        color: '#666666',
                                                        textDecoration: 'none'
                                                    })
                                                }}
                                            >
                                                {COMPLIANCE.DEALER_TRACK.LINK_TEXT}
                                            </Link>
                                            .
                                        </Typography>
                                    )}
                                </ComplianceLinkContainer>
                            </ContainerBox>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </AccordionGroup>
    );
};
